import {Fragment, useCallback, useEffect, useMemo, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Container,
    FormControl,
    IconButton,
    InputLabel,
    ListItemText,
    Menu,
    MenuItem,
    OutlinedInput,
    Paper,
    Select,
    Snackbar,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PageTitle from '../components/PageTitle';
import api from '../hooks/useApi';
import {format, parseISO} from 'date-fns';
import {
    getCachedBookings,
    getCacheKey,
    patchCachedBookingsStatus,
    setCachedBookings,
} from '../utils/bookingsCache';
import {isSuperAdmin} from '../auth/permissions';
import AuthProvider from '../hooks/authProvider';

const STATUS_COLOR_MAP = {
    '1': 'warning',
    '2': 'error',
    '3': 'warning',
    '4': 'success',
    '5': 'info',
    '6': 'secondary',
    '7': 'default',
};
const BOOKING_SOURCE_LABELS = {'0': 'Закрито', '1': 'Менеджер каналів', '2': 'Hotels24', '3': 'Booking.com', '4': 'Овербукінг'};

const DATE_FIELD_OPTIONS = [
    {value: 'created_at', label: 'Створено'},
    {value: 'booking_from', label: 'Заїзд'},
    {value: 'booking_to', label: 'Виїзд'},
];

const formatDate = (value) => {
    if (!value) {
        return '—';
    }

    try {
        return format(parseISO(value), 'dd.MM.yyyy HH:mm');
    } catch {
        return value;
    }
};

const zeroPad = (num, places = 5) => String(num).padStart(places, '0');

export default function OrdersPage() {
    const [rows, setRows] = useState([]);
    const [count, setCount] = useState(0);
    const [numPages, setNumPages] = useState(0);
    const [statusChoices, setStatusChoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [detailsById, setDetailsById] = useState({});
    const [expandedId, setExpandedId] = useState(null);

    const [filters, setFilters] = useState({
        dateField: 'created_at',
        dateFrom: '',
        dateTo: '',
        statuses: [],
        search: '',
    });
    const [draftFilters, setDraftFilters] = useState(filters);

    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
    const [statusMenuBooking, setStatusMenuBooking] = useState(null);

    const normalizeDate = (v) => {
        if (!v) return '';
        // если уже yyyy-mm-dd
        if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
        // если dd/mm/yyyy
        const m = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (m) return `${m[3]}-${m[2]}-${m[1]}`;
        return v;
    };

    const [snackbar, setSnackbar] = useState({open: false, severity: 'success', message: ''});
    const canChangeStatus = isSuperAdmin(AuthProvider.user);

    const activeParams = useMemo(() => ({
        dateField: filters.dateField,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        statuses: [...filters.statuses].sort(),
        search: filters.search,
        page: page + 1,
        pageSize,
    }), [filters, page, pageSize]);

    const cacheKey = useMemo(() => getCacheKey(activeParams), [activeParams]);

    const fetchBookings = useCallback(async () => {
        if (filters.dateFrom && filters.dateTo && filters.dateTo < filters.dateFrom) {
            setSnackbar({open: true, severity: 'error', message: 'Дата "до" не може бути меншою за дату "від"'});
            return;
        }

        const cached = getCachedBookings(cacheKey);
        if (cached) {
            setRows(cached.results || []);
            setCount(cached.count || 0);
            setNumPages(cached.num_pages || 0);
            setStatusChoices(cached.status_choices || []);
            return;
        }

        setLoading(true);
        try {
            const params = {
                date_field: filters.dateField,
                page: page + 1,
                page_size: pageSize,
                ordering: '-created_at',
            };

            if (filters.dateFrom) params.date_from = normalizeDate(filters.dateFrom);
            if (filters.dateTo) params.date_to = normalizeDate(filters.dateTo);

            if (filters.dateFrom) {
                params.date_from = filters.dateFrom;
            }
            if (filters.dateTo) {
                params.date_to = filters.dateTo;
            }
            if (filters.search) {
                params.search = filters.search;
            }

            const response = await api.get('/api/booking_modules/use/booking/list/', {
                params: {
                    ...params,
                    status: (filters.statuses || []).join(','),
                },
            });

            setRows(response.data.results || []);
            setCount(response.data.count || 0);
            setNumPages(response.data.num_pages || 0);
            setStatusChoices(response.data.status_choices || []);
            setCachedBookings(cacheKey, response.data);
        } catch (error) {
            setSnackbar({open: true, severity: 'error', message: 'Не вдалося завантажити бронювання'});
        } finally {
            setLoading(false);
        }
    }, [cacheKey, filters, page, pageSize]);

    useEffect(() => {
        fetchBookings();
    }, [fetchBookings]);

    const statusLabelMap = useMemo(() => Object.fromEntries(statusChoices.map(({
                                                                                   value,
                                                                                   label
                                                                               }) => [value, label])), [statusChoices]);

    const handleApplyFilters = () => {
        if (draftFilters.dateFrom && draftFilters.dateTo && draftFilters.dateTo < draftFilters.dateFrom) {
            setSnackbar({open: true, severity: 'error', message: 'Дата "до" не може бути меншою за дату "від"'});
            return;
        }

        setPage(0);
        setFilters(draftFilters);
    };

    const handleToggleExpand = useCallback(async (bookingId) => {
        setExpandedId((prev) => (prev === bookingId ? null : bookingId));

        if (detailsById[bookingId]) return;

        try {
            const response = await api.get(`/api/booking_modules/use/booking/list/${bookingId}/`);
            setDetailsById((prev) => ({
                ...prev,
                [bookingId]: response.data.data,
            }));
        } catch {
            setSnackbar({open: true, severity: 'error', message: 'Не вдалося завантажити деталі бронювання'});
        }
    }, [detailsById]);


    const openStatusMenu = (event, booking) => {
        setStatusMenuAnchor(event.currentTarget);
        setStatusMenuBooking(booking);
    };

    const closeStatusMenu = () => {
        setStatusMenuAnchor(null);
        setStatusMenuBooking(null);
    };

    const handleStatusChange = async (newStatus) => {
        if (!statusMenuBooking || !canChangeStatus) {
            return;
        }

        const bookingId = statusMenuBooking.id;
        const previousStatus = statusMenuBooking.status;

        setRows((prev) => prev.map((row) => (row.id === bookingId ? {...row, status: newStatus} : row)));
        patchCachedBookingsStatus(bookingId, newStatus);
        closeStatusMenu();

        try {
            await api.patch('/api/booking_modules/use/booking/update-status/', {booking_id: bookingId, status: newStatus});
            setSnackbar({open: true, severity: 'success', message: 'Статус оновлено'});
        } catch {
            setRows((prev) => prev.map((row) => (row.id === bookingId ? {...row, status: previousStatus} : row)));
            patchCachedBookingsStatus(bookingId, previousStatus);
            setSnackbar({open: true, severity: 'error', message: 'Помилка оновлення статусу'});
        }
    };

    return (
        <Container maxWidth="xl" sx={{pt: 4, pb: 4}}>
            <PageTitle title="Бронювання"/>

            <Paper sx={{p: 2, mb: 2}}>
                <Stack direction={{xs: 'column', md: 'row'}} spacing={2} alignItems="center">
                    <FormControl size="small" sx={{minWidth: 170}}>
                        <InputLabel id="date-field-label">Дата</InputLabel>
                        <Select
                            labelId="date-field-label"
                            value={draftFilters.dateField}
                            label="Дата"
                            onChange={(event) => setDraftFilters((prev) => ({...prev, dateField: event.target.value}))}
                        >
                            {DATE_FIELD_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label="Від"
                        type="date"
                        size="small"
                        InputLabelProps={{shrink: true}}
                        value={draftFilters.dateFrom}
                        onChange={(event) => setDraftFilters((prev) => ({...prev, dateFrom: event.target.value}))}
                    />

                    <TextField
                        label="До"
                        type="date"
                        size="small"
                        InputLabelProps={{shrink: true}}
                        value={draftFilters.dateTo}
                        onChange={(event) => setDraftFilters((prev) => ({...prev, dateTo: event.target.value}))}
                    />

                    <FormControl size="small" sx={{minWidth: 240}}>
                        <InputLabel id="status-filter-label">Статуси</InputLabel>
                        <Select
                            labelId="status-filter-label"
                            multiple
                            value={draftFilters.statuses}
                            onChange={(event) => setDraftFilters((prev) => ({...prev, statuses: event.target.value}))}
                            input={<OutlinedInput label="Статуси"/>}
                            renderValue={(selected) => selected.map((item) => statusLabelMap[item] || item).join(', ')}
                        >
                            {statusChoices.map((statusOption) => (
                                <MenuItem key={statusOption.value} value={statusOption.value}>
                                    <ListItemText primary={statusOption.label}/>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        size="small"
                        label="Пошук (ID/гiсть/телефон)"
                        value={draftFilters.search}
                        onChange={(event) => setDraftFilters((prev) => ({...prev, search: event.target.value}))}
                    />

                    <Button variant="contained" onClick={handleApplyFilters}>Застосувати</Button>
                </Stack>
            </Paper>

            <Paper>
                {loading ? (
                    <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}>
                        <CircularProgress/>
                    </Box>
                ) : (
                    <>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell/>
                                    <TableCell>Номер</TableCell>
                                    <TableCell>Гість</TableCell>
                                    <TableCell>Заїзд</TableCell>
                                    <TableCell>Виїзд</TableCell>
                                    <TableCell>Номер / тип</TableCell>
                                    <TableCell>Статус</TableCell>
                                    <TableCell>Ціна</TableCell>
                                    <TableCell>Створено</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row) => {
                                    const isExpanded = expandedId === row.id;
                                    const firstChild = row.hotel_children?.[0];
                                    return (
                                        <Fragment key={row.id}>
                                            <TableRow key={row.id} hover>
                                                <TableCell>
                                                    <IconButton size="small" onClick={() => handleToggleExpand(row.id)}>
                                                        {isExpanded ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/>}
                                                    </IconButton>
                                                </TableCell>
                                                <TableCell>{zeroPad(row.id)}</TableCell>
                                                <TableCell>{row.last_name} {row.first_name}<br/>{row.phone}</TableCell>
                                                <TableCell>{formatDate(row.booking_from)}</TableCell>
                                                <TableCell>{formatDate(row.booking_to)}</TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{row.hotel?.name || '—'}</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {firstChild?.classification?.name || firstChild?.name || 'Інформація відсутня'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Tooltip title={canChangeStatus ? "Змінити статус" : "Статус доступний лише для перегляду"}>
                                                        <Chip
                                                            clickable={canChangeStatus}
                                                            size="small"
                                                            color={STATUS_COLOR_MAP[row.status] || 'default'}
                                                            label={statusLabelMap[row.status] || `Невідомий (${row.status || '—'})`}
                                                            onClick={canChangeStatus ? (event) => openStatusMenu(event, row) : undefined}
                                                        />
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell>{row.price || '—'}</TableCell>
                                                <TableCell>{formatDate(row.created_at)}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell colSpan={9} sx={{py: 0}}>
                                                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                        <Box sx={{p: 2, backgroundColor: '#fafafa'}}>
                                                            <Typography
                                                                variant="body2"><b>Коментар:</b> {detailsById[row.id]?.comments || '—'}
                                                            </Typography>
                                                            <Typography variant="body2"><b>Час
                                                                заїзду:</b> {detailsById[row.id]?.time_arrived || row.time_arrived || '—'}
                                                            </Typography>
                                                            <Typography
                                                                variant="body2"><b>Джерело:</b> {BOOKING_SOURCE_LABELS[detailsById[row.id]?.booking_system_from || row.booking_system_from] || '—'}
                                                            </Typography>
                                                        </Box>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        </Fragment>
                                    );
                                })}
                            </TableBody>
                        </Table>

                        <TablePagination
                            component="div"
                            count={count}
                            page={page}
                            onPageChange={(_, newPage) => setPage(newPage)}
                            rowsPerPage={pageSize}
                            onRowsPerPageChange={(event) => {
                                setPageSize(parseInt(event.target.value, 10));
                                setPage(0);
                            }}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            labelDisplayedRows={({from, to, count: total}) => `${from}-${to} з ${total}`}
                            labelRowsPerPage="На сторінці"
                        />
                        <Box sx={{px: 2, pb: 2}}>
                            <Typography variant="caption" color="text.secondary">Сторінок: {numPages}</Typography>
                        </Box>
                    </>
                )}
            </Paper>

            <Menu
                anchorEl={statusMenuAnchor}
                open={Boolean(statusMenuAnchor)}
                onClose={closeStatusMenu}
            >
                {statusChoices.map((choice) => (
                    <MenuItem key={choice.value} onClick={() => handleStatusChange(choice.value)}>
                        {choice.label}
                    </MenuItem>
                ))}
            </Menu>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((prev) => ({...prev, open: false}))}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({...prev, open: false}))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
}

// ✅ Совместимость с App.js (React Router data APIs)
export async function loader() {
    // Если раньше loader что-то грузил — можно вернуть пустое и грузить внутри компонента, как сейчас
    return null;
}

export async function action() {
    return null;
}

// Если App.js вызывает mutateOrder где-то отдельно — оставляем функцию-обёртку
export async function mutateOrder({booking_id, status}) {
    // Важно: тут нужно использовать тот же api, что и в компоненте.
    // Но hooks использовать нельзя вне React компонентов, поэтому api должен быть НЕ хуком.
    // Если ../hooks/useApi — это хук, то тут нельзя его вызывать.
    // Тогда делаем fetch напрямую:

    const res = await api.patch('/api/booking_modules/use/booking/update-status/', {
        booking_id,
        status
    });

    if (res.status < 200 || res.status >= 300) {
        throw new Error('Failed to update status');
    }
    return true;
}
